import React, { useState, useEffect } from 'react';
import HierarchyTree from './HierarchyTree';
import { OccupationHierarchy, OccupationHierarchyEditorProps } from './types';
import { DragEndEvent } from '@dnd-kit/core';

// TypeScript型定義
declare global {
  interface Window {
    showGlobalFlash?: (message: string, type: string, htmlSafe: boolean) => void;
  }
}

const OccupationHierarchyEditor: React.FC<OccupationHierarchyEditorProps> = ({
  hierarchyDataUrl,
  updateHierarchyUrl,
  createOccupationUrl,
  updateOccupationUrlTemplate,
  deleteOccupationUrlTemplate,
  togglePublishedUrlTemplate,
  csrfToken,
}) => {
  // deleteOccupationUrlTemplateから関数を作成
  const deleteOccupationUrl = (id: number): string => {
    return deleteOccupationUrlTemplate.replace(':id', id.toString());
  };
  // updateOccupationUrlTemplateから関数を作成
  const updateOccupationUrl = (id: number): string => {
    return updateOccupationUrlTemplate.replace(':id', id.toString());
  };
  // togglePublishedUrlTemplateから関数を作成
  const togglePublishedUrl = (id: number): string => {
    return togglePublishedUrlTemplate.replace(':id', id.toString());
  };
  const [occupations, setOccupations] = useState<OccupationHierarchy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({
    type: null,
    message: '',
  });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOccupationName, setNewOccupationName] = useState('');
  const [newOccupationParentId, setNewOccupationParentId] = useState<number | null>(null);
  const [parentSearchTerm, setParentSearchTerm] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);

  useEffect(() => {
    fetchHierarchyData();
  }, []);

  const fetchHierarchyData = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(hierarchyDataUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error('階層データの取得に失敗しました');
      }

      const data = await response.json();
      setOccupations(data.occupations || []);
    } catch (error) {
      console.error('Error fetching hierarchy data:', error);
      setSaveStatus({
        type: 'error',
        message: '階層データの取得に失敗しました',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const updateHierarchy = async (
    occupationId: number,
    parentId: number | null,
    display_priority: number
  ) => {
    try {
      setIsSaving(true);

      const response = await fetch(updateHierarchyUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          occupation_id: occupationId,
          parent_id: parentId,
          display_priority: display_priority,
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.errors?.join(', ') || '階層の更新に失敗しました');
      }

      // トースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(data.message || '階層を更新しました', 'success', false);
      }

      // ページをリロードして反映
      window.location.reload();
    } catch (error) {
      console.error('Error updating hierarchy:', error);
      const errorMessage = error instanceof Error ? error.message : '階層の更新に失敗しました';
      
      // エラーのトースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(errorMessage, 'error', false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const updateOccupation = async (occupationId: number, name: string, display_priority?: number) => {
    try {
      setIsSaving(true);

      const updateData: { name: string; display_priority?: number } = { name };
      if (display_priority !== undefined) {
        updateData.display_priority = display_priority;
      }

      const response = await fetch(updateOccupationUrl(occupationId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          occupation: updateData,
        }),
      });

      // レスポンスがJSONでない場合（HTMLエラーページなど）をチェック
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error('サーバーエラーが発生しました。レスポンスがJSON形式ではありません。');
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.errors?.join(', ') || '職種の更新に失敗しました');
      }

      // トースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(data.message || '職種を更新しました', 'success', false);
      }

      // ページをリロードして反映
      window.location.reload();
    } catch (error) {
      console.error('Error updating occupation:', error);
      const errorMessage = error instanceof Error ? error.message : '職種の更新に失敗しました';
      
      // エラーのトースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(errorMessage, 'error', false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const deleteOccupation = async (occupationId: number) => {
    try {
      setIsSaving(true);

      const response = await fetch(deleteOccupationUrl(occupationId), {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      });

      // レスポンスがJSONかどうかを確認
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('サーバーから予期しない形式のレスポンスが返されました');
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.errors?.join(', ') || data.error || '職種の削除に失敗しました');
      }

      // ローカルステートから削除
      setOccupations((prev) => prev.filter((occ) => occ.id !== occupationId));

      // トースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(data.message || '職種を削除しました', 'success', false);
      }
    } catch (error) {
      console.error('Error deleting occupation:', error);
      const errorMessage = error instanceof Error ? error.message : '職種の削除に失敗しました';
      
      // エラーのトースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(errorMessage, 'error', false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const togglePublished = async (occupationId: number) => {
    try {
      const response = await fetch(togglePublishedUrl(occupationId), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'same-origin',
      });

      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('サーバーから予期しない形式のレスポンスが返されました');
      }

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.errors?.join(', ') || '公開ステータスの更新に失敗しました');
      }

      // ローカルステートを更新
      setOccupations((prev) =>
        prev.map((occ) =>
          occ.id === occupationId ? { ...occ, published: data.published } : occ
        )
      );

      // トースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(data.message || '公開ステータスを更新しました', 'success', false);
      }
    } catch (error) {
      console.error('Error toggling published status:', error);
      const errorMessage = error instanceof Error ? error.message : '公開ステータスの更新に失敗しました';
      
      // エラーのトースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(errorMessage, 'error', false);
      }
    }
  };

  const createOccupation = async () => {
    if (!newOccupationName.trim()) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('職種名を入力してください', 'error', false);
      }
      return;
    }

    try {
      setIsSaving(true);
      setSaveStatus({ type: null, message: '' });

      const formData = new FormData();
      formData.append('occupation[name]', newOccupationName.trim());
      if (newOccupationParentId) {
        formData.append('occupation[parent_id]', newOccupationParentId.toString());
      }

      const response = await fetch(createOccupationUrl, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
        },
        credentials: 'same-origin',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.errors?.join(', ') || '職種の追加に失敗しました');
      }

      // ローカルステートに追加
      setOccupations((prev) => [...prev, data.occupation]);

      // フォームをリセット
      setNewOccupationName('');
      setNewOccupationParentId(null);
      setParentSearchTerm('');
      setShowParentDropdown(false);
      setShowAddForm(false);

      // トースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(data.message || '職種を追加しました', 'success', false);
      }
    } catch (error) {
      console.error('Error creating occupation:', error);
      const errorMessage = error instanceof Error ? error.message : '職種の追加に失敗しました';
      
      // エラーのトースト通知を表示
      if (window.showGlobalFlash) {
        window.showGlobalFlash(errorMessage, 'error', false);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeId = active.id as number;
    const overId = over.id as string | number;

    const activeOccupation = occupations.find((occ) => occ.id === activeId);
    if (!activeOccupation) {
      return;
    }

    // ドロップゾーンへのドロップの場合
    if (typeof overId === 'string' && overId.startsWith('drop-')) {
      // 第一階層へのドロップ
      if (overId === 'drop-first-level') {
        const siblings = occupations.filter(
          (occ) => occ.parent_id === null && occ.id !== activeId
        );
        // 降順: 兄弟の数に基づいてdisplay_priorityを計算
        // 既存の職種の最大display_priorityを取得し、それより大きい値を設定
        const maxExistingPriority = siblings.length > 0
          ? Math.max(...siblings.map((occ) => occ.display_priority))
          : 0;
        const newDisplayPriority = Math.max(maxExistingPriority + 10, 100);
        await updateHierarchy(activeId, null, newDisplayPriority);
        return;
      }

      // 他のノードへのドロップ
      const targetNodeId = parseInt(overId.replace('drop-', ''), 10);
      const targetNode = occupations.find((occ) => occ.id === targetNodeId);
      if (!targetNode) return;

      // 自分自身を親にすることはできない（循環参照防止）
      if (targetNodeId === activeId) return;

      // ターゲットがアクティブな職種の子孫でないことを確認
      const isDescendant = (parentId: number | null, childId: number): boolean => {
        if (!parentId) return false;
        if (parentId === childId) return true;
        const parent = occupations.find((occ) => occ.id === parentId);
        if (!parent) return false;
        return isDescendant(parent.parent_id, childId);
      };

      if (isDescendant(activeId, targetNodeId)) {
        if (window.showGlobalFlash) {
          window.showGlobalFlash('循環参照を防ぐため、子孫を親にすることはできません', 'error', false);
        }
        return;
      }

      // 同じ親内の兄弟を取得して位置を決定
      const siblings = occupations.filter(
        (occ) => occ.parent_id === targetNodeId && occ.id !== activeId
      );
      // 降順: 既存の職種の最大display_priorityを取得し、それより大きい値を設定
      const maxExistingPriority = siblings.length > 0
        ? Math.max(...siblings.map((occ) => occ.display_priority))
        : 0;
      const newDisplayPriority = Math.max(maxExistingPriority + 10, 100);

      await updateHierarchy(activeId, targetNodeId, newDisplayPriority);
      return;
    }

    // ノードへのドロップの場合（並び替え）
    if (typeof overId === 'number') {
      const overOccupation = occupations.find((occ) => occ.id === overId);
      if (!overOccupation) return;

      // 同じ親内での並び替え
      if (activeOccupation.parent_id === overOccupation.parent_id) {
        const siblings = occupations
          .filter((occ) => occ.parent_id === activeOccupation.parent_id)
          .sort((a, b) => b.display_priority - a.display_priority);
        const activeIndex = siblings.findIndex((occ) => occ.id === activeId);
        const overIndex = siblings.findIndex((occ) => occ.id === overId);

        if (activeIndex === overIndex) return;

        // 降順: ターゲット位置のdisplay_priorityより大きい値を設定
        // ターゲット位置が上（優先度が高い）に移動する場合は、ターゲットのdisplay_priority + 1
        // ターゲット位置が下（優先度が低い）に移動する場合は、ターゲットのdisplay_priority - 1
        let newDisplayPriority: number;
        if (activeIndex < overIndex) {
          // 下に移動: ターゲットのdisplay_priorityより小さい値
          newDisplayPriority = Math.max(overOccupation.display_priority - 1, 0);
        } else {
          // 上に移動: ターゲットのdisplay_priorityより大きい値
          const maxExistingPriority = siblings.length > 0
            ? Math.max(...siblings.map((occ) => occ.display_priority))
            : 0;
          newDisplayPriority = Math.max(overOccupation.display_priority + 1, maxExistingPriority + 10);
        }
        await updateHierarchy(activeId, activeOccupation.parent_id, newDisplayPriority);
        return;
      }

      // 異なる親への移動（親の下に配置）
      const siblings = occupations.filter(
        (occ) => occ.parent_id === overOccupation.parent_id && occ.id !== activeId
      );
      // 降順: 既存の職種の最大display_priorityを取得し、それより大きい値を設定
      const maxExistingPriority = siblings.length > 0
        ? Math.max(...siblings.map((occ) => occ.display_priority))
        : 0;
      const newDisplayPriority = Math.max(maxExistingPriority + 10, 100);
      await updateHierarchy(activeId, overOccupation.parent_id, newDisplayPriority);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">読み込み中...</span>
        </div>
        <p className="mt-3">階層データを読み込んでいます...</p>
      </div>
    );
  }

  return (
    <div>
      {isSaving && (
        <div className="alert alert-info">
          <div className="spinner-border spinner-border-sm me-2" role="status">
            <span className="visually-hidden">保存中...</span>
          </div>
          保存中...
        </div>
      )}

      <div className="mb-3 d-flex justify-content-between align-items-center">
        <p className="text-muted mb-0">
          ドラッグ＆ドロップで職種の階層を設定できます。職種を親職種の下にドロップすることで、親子関係を設定できます。
          左側の矢印アイコンをクリックすると、子職種の表示/非表示を切り替えられます。
        </p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <i className="bi bi-plus-circle me-1"></i>
          職種を追加
        </button>
      </div>

      {showAddForm && (
        <div className="card mb-3">
          <div className="card-body">
            <h5 className="card-title">新しい職種を追加</h5>
            <div className="row g-3">
              <div className="col-md-6">
                <label htmlFor="newOccupationName" className="form-label">
                  職種名 <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  id="newOccupationName"
                  value={newOccupationName}
                  onChange={(e) => setNewOccupationName(e.target.value)}
                  placeholder="例: ウェブマーケター"
                />
              </div>
              <div className="col-md-6">
                <label htmlFor="newOccupationParent" className="form-label">
                  親職種（オプション）
                </label>
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control"
                    id="newOccupationParent"
                    placeholder={newOccupationParentId ? '親職種を検索...' : '親職種を検索...'}
                    value={newOccupationParentId 
                      ? occupations.find(occ => occ.id === newOccupationParentId)?.name || parentSearchTerm
                      : parentSearchTerm}
                    onChange={(e) => {
                      setParentSearchTerm(e.target.value);
                      setShowParentDropdown(true);
                      // 検索開始時に選択をクリア
                      if (newOccupationParentId) {
                        setNewOccupationParentId(null);
                      }
                    }}
                    onFocus={() => {
                      setShowParentDropdown(true);
                      // フォーカス時に選択された職種名を検索テキストに設定
                      if (newOccupationParentId) {
                        const selected = occupations.find(occ => occ.id === newOccupationParentId);
                        if (selected) {
                          setParentSearchTerm(selected.name);
                        }
                      }
                    }}
                    onBlur={() => {
                      // 少し遅延させてから閉じる（クリックイベントを処理するため）
                      setTimeout(() => setShowParentDropdown(false), 200);
                    }}
                  />
                  {newOccupationParentId && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary position-absolute"
                      style={{ right: '5px', top: '50%', transform: 'translateY(-50%)' }}
                      onClick={(e) => {
                        e.preventDefault();
                        setNewOccupationParentId(null);
                        setParentSearchTerm('');
                      }}
                    >
                      ×
                    </button>
                  )}
                  {showParentDropdown && (
                    <div
                      className="position-absolute w-100 bg-white border rounded shadow-lg"
                      style={{ zIndex: 1000, maxHeight: '300px', overflowY: 'auto', top: '100%', marginTop: '2px' }}
                    >
                      <div
                        className="p-2 border-bottom cursor-pointer"
                        style={{ cursor: 'pointer' }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setNewOccupationParentId(null);
                          setParentSearchTerm('');
                          setShowParentDropdown(false);
                        }}
                      >
                        <small className="text-muted">なし（第1階層）</small>
                      </div>
                      {occupations
                        .filter((occ) => {
                          if (occ.level > 2) return false;
                          if (!parentSearchTerm.trim()) return true;
                          return occ.name.toLowerCase().includes(parentSearchTerm.toLowerCase());
                        })
                        .slice(0, 20)
                        .map((occ) => {
                          const selected = occ.id === newOccupationParentId;
                          return (
                            <div
                              key={occ.id}
                              className={`p-2 border-bottom ${selected ? 'bg-primary text-white' : ''}`}
                              style={{ cursor: 'pointer' }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                setNewOccupationParentId(occ.id);
                                setParentSearchTerm(occ.name);
                                setShowParentDropdown(false);
                              }}
                            >
                              <span style={{ marginLeft: `${(occ.level - 1) * 20}px` }}>
                                {occ.name}
                              </span>
                            </div>
                          );
                        })}
                      {occupations.filter((occ) => {
                        if (occ.level > 2) return false;
                        if (!parentSearchTerm.trim()) return true;
                        return occ.name.toLowerCase().includes(parentSearchTerm.toLowerCase());
                      }).length === 0 && (
                        <div className="p-2 text-muted">
                          <small>該当する職種が見つかりません</small>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-3">
              <button
                type="button"
                className="btn btn-success me-2"
                onClick={createOccupation}
                disabled={isSaving || !newOccupationName.trim()}
              >
                {isSaving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    追加中...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-1"></i>
                    追加
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddForm(false);
                  setNewOccupationName('');
                  setNewOccupationParentId(null);
                }}
                disabled={isSaving}
              >
                キャンセル
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body">
      <HierarchyTree
        occupations={occupations}
        onDragEnd={handleDragEnd}
        onUpdateHierarchy={updateHierarchy}
        onUpdateOccupation={updateOccupation}
        onDeleteOccupation={deleteOccupation}
        onTogglePublished={togglePublished}
      />
        </div>
      </div>
    </div>
  );
};

export default OccupationHierarchyEditor;

